import React from 'react';
import {
  CurrencyEur, ShieldWarning, CheckCircle, Warning,
  TrendDown, Factory, Truck, UsersThree, ChartPieSlice
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const STATUS_COLORS = {
  'Actual Verified': '#4ade80',
  'Actual Unverified': '#facc15',
  'Supplier-Declared': '#60a5fa',
  'Pending Review': '#f97316',
  'Default Applied': '#ef4444',
  'Non-Compliant': '#991b1b',
};

const SEVERITY_STYLE = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
  warning: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  info: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
};

function fmt(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n?.toLocaleString?.() ?? n;
}

function KpiTile({ icon: Icon, label, value, sub, color = 'bg-primary/10', textColor = 'text-primary' }) {
  return (
    <div className="bg-surface border border-border rounded-lg p-4 hover:border-tertiary/40 transition-all duration-200" data-testid={`cbam-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center shrink-0`}>
          <Icon size={18} weight="duotone" className={textColor} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-body font-semibold uppercase tracking-[0.12em] text-text-secondary truncate">{label}</p>
          <p className="font-heading text-xl font-bold text-text-primary mt-0.5">{value}</p>
          {sub && <p className="text-xs font-body text-text-secondary mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

export default function CbamExecutive({ data }) {
  if (!data) return null;
  const { kpis, shipment_status_breakdown, top_products, top_customers, declarations, alerts, emissions } = data;

  const statusData = Object.entries(shipment_status_breakdown || {}).map(([name, value]) => ({
    name, value, fill: STATUS_COLORS[name] || '#6b7280',
  }));

  const productBarData = (top_products || []).map(p => ({
    name: p.desc?.length > 18 ? p.desc.slice(0, 18) + '...' : p.desc,
    liability: p.net_eur,
    tonnes: p.tonnes,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cbam-executive">
      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={CurrencyEur} label="EU Revenue at Risk" value={`EUR ${fmt(kpis.eu_revenue_at_risk_eur)}`} sub={`${kpis.products_in_scope} products in scope`} color="bg-amber-500/10" textColor="text-amber-400" />
        <KpiTile icon={ShieldWarning} label="Net CBAM Liability" value={`EUR ${fmt(kpis.net_cbam_liability_eur)}`} sub={`Gross EUR ${fmt(kpis.gross_cbam_liability_eur)} - Deductions EUR ${fmt(kpis.carbon_deductions_eur)}`} color="bg-red-500/10" textColor="text-red-400" />
        <KpiTile icon={CheckCircle} label="Verified Data Coverage" value={`${kpis.verified_data_coverage_pct}%`} sub={`${kpis.total_shipments} total EU shipments`} color="bg-emerald-500/10" textColor="text-emerald-400" />
        <KpiTile icon={Warning} label="Default Value Exposure" value={`${kpis.default_value_exposure_pct}%`} sub="Using CBAM default values" color="bg-orange-500/10" textColor="text-orange-400" />
        <KpiTile icon={Truck} label="EU Volume" value={`${fmt(kpis.eu_volume_tonnes)} t`} sub="Tonnes shipped to EU under CBAM" color="bg-blue-500/10" textColor="text-blue-400" />
        <KpiTile icon={UsersThree} label="Supplier Response" value={`${kpis.supplier_response_rate_pct}%`} sub="Active data sharing" color="bg-violet-500/10" textColor="text-violet-400" />
        <KpiTile icon={TrendDown} label="Opportunity Savings" value={`EUR ${fmt(kpis.opportunity_savings_eur)}`} sub="From decarbonisation scenarios" color="bg-teal-500/10" textColor="text-teal-400" />
        <KpiTile icon={Factory} label="Future Scope Watch" value={`${kpis.products_future_scope} products`} sub="Proposed 2028 downstream expansion" color="bg-slate-500/10" textColor="text-slate-400" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Shipment Status Pie */}
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-status-chart">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Shipment Data Status Breakdown</h3>
          <div className="flex items-center gap-6">
            <div className="w-40 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={2}>
                    {statusData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5">
              {statusData.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs font-body">
                  <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.fill }} />
                  <span className="text-text-secondary">{s.name}</span>
                  <span className="font-semibold text-text-primary ml-auto">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Products Bar */}
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-top-products-chart">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Top Products by CBAM Liability (EUR)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={productBarData} layout="vertical" margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={120} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`EUR ${v.toLocaleString()}`, 'Net Liability']} />
              <Bar dataKey="liability" fill="#f97316" radius={[0, 4, 4, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Declarations + Top Customers Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Declaration Readiness */}
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-declarations">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Declaration Periods</h3>
          <div className="space-y-3">
            {(declarations || []).map((d, i) => (
              <div key={i} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-body font-semibold text-text-primary">{d.period}</span>
                  <span className={`text-xs font-body px-2 py-0.5 rounded-full ${d.status === 'In Preparation' ? 'bg-amber-500/10 text-amber-400' : 'bg-blue-500/10 text-blue-400'}`}>{d.status}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs font-body mb-3">
                  <div><span className="text-text-secondary">Shipments</span><p className="font-semibold text-text-primary">{d.total_shipments}</p></div>
                  <div><span className="text-text-secondary">Net Liability</span><p className="font-semibold text-text-primary">EUR {fmt(d.net_liability_eur)}</p></div>
                  <div><span className="text-text-secondary">Deadline</span><p className="font-semibold text-text-primary">{d.deadline}</p></div>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${d.declaration_readiness_pct >= 70 ? 'bg-emerald-500' : d.declaration_readiness_pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${d.declaration_readiness_pct}%` }} />
                </div>
                <p className="text-[11px] font-body text-text-secondary mt-1">Readiness: {d.declaration_readiness_pct}%</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top Customers */}
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-top-customers">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Top EU Customers by CBAM Exposure</h3>
          <div className="space-y-2">
            {(top_customers || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                <div>
                  <p className="text-sm font-body font-medium text-text-primary">{c.name}</p>
                  <p className="text-xs font-body text-text-secondary">{c.country} — {c.shipments} shipment{c.shipments > 1 ? 's' : ''}</p>
                </div>
                <span className="text-sm font-heading font-bold text-amber-400">EUR {fmt(c.net_eur)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-alerts">
        <h3 className="text-sm font-body font-semibold text-text-primary mb-4">CBAM Compliance Alerts</h3>
        <div className="space-y-2">
          {(alerts || []).map((a, i) => {
            const sty = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.info;
            return (
              <div key={i} className={`border ${sty.border} rounded-lg p-3 ${sty.bg}`} data-testid={`cbam-alert-${a.alert_id}`}>
                <div className="flex items-start gap-2">
                  <ShieldWarning size={16} weight="fill" className={`${sty.text} mt-0.5 shrink-0`} />
                  <div>
                    <p className={`text-sm font-body font-semibold ${sty.text}`}>{a.title}</p>
                    <p className="text-xs font-body text-text-secondary mt-1">{a.message}</p>
                    <p className="text-xs font-body text-text-secondary mt-1 italic">Action: {a.action}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Emissions Intensity Table */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-emissions-table">
        <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Emissions Intensity by Product (tCO2e/t)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-body">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-text-secondary font-semibold">Product</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Direct</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Indirect</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Precursor</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Total</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Default</th>
                <th className="text-right py-2 text-text-secondary font-semibold">vs Default</th>
                <th className="text-center py-2 text-text-secondary font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {(emissions || []).map((e, i) => (
                <tr key={i} className="border-b border-border/50 hover:bg-white/[0.02]">
                  <td className="py-2.5 text-text-primary font-medium">{e.product_desc}</td>
                  <td className="py-2.5 text-right text-text-secondary">{e.direct_tco2e_per_t}</td>
                  <td className="py-2.5 text-right text-text-secondary">{e.indirect_tco2e_per_t}</td>
                  <td className="py-2.5 text-right text-text-secondary">{e.precursor_tco2e_per_t}</td>
                  <td className="py-2.5 text-right font-semibold text-text-primary">{e.total_intensity}</td>
                  <td className="py-2.5 text-right text-text-secondary">{e.sector_default_intensity}</td>
                  <td className={`py-2.5 text-right font-semibold ${e.delta_vs_default_pct < 0 ? 'text-emerald-400' : e.delta_vs_default_pct > 0 ? 'text-red-400' : 'text-text-secondary'}`}>
                    {e.delta_vs_default_pct > 0 ? '+' : ''}{e.delta_vs_default_pct}%
                  </td>
                  <td className="py-2.5 text-center">
                    {e.verified_flag ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] font-semibold uppercase"><CheckCircle size={12} weight="fill" /> Verified</span>
                    ) : e.actual_flag ? (
                      <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] font-semibold uppercase"><Warning size={12} weight="fill" /> Unverified</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-400 text-[10px] font-semibold uppercase"><ShieldWarning size={12} weight="fill" /> Default</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
